import type { TimeSlots } from 'api/types/timeSlot.types';
import { useEffect, useRef, useState } from 'react';

export interface SelectedSlots {
  dateSlotId: string;
  timeSlotId: string;
}

const initialSelectedSlotsState: SelectedSlots = {
  dateSlotId: '',
  timeSlotId: '',
};

export function useTimeSlotSelector({
  timeslotsData,
  selectedDateTimeSlot,
  show,
  seletedSlots,
}: {
  timeslotsData: TimeSlots.TimeslotsData | undefined;
  selectedDateTimeSlot?: TimeSlots.Timeslot;
  show: boolean;
  seletedSlots?: SelectedSlots;
}) {
  // allow preselect the selectedSlots
  const [selectedSlots, setSelectedSlots] = useState(seletedSlots ?? initialSelectedSlotsState);
  const dateRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const slotRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [isSlotsLoaded, setIsSlotsLoaded] = useState(false);

  useEffect(() => {
    setSelectedSlots((prev) => {
      // if selected time slot is not available for the new date selected, clear selected timeslot, else reselect it.
      const currentDateTimeSlots = timeslotsData?.dates?.find((d) => d.date === prev.dateSlotId)?.timeslots;

      const isAvailable = currentDateTimeSlots?.find((t) => t.slotId === prev.timeSlotId)?.isAvailable;

      if (isAvailable) {
        return prev;
      }

      if (prev.dateSlotId && !prev.timeSlotId) {
        return prev;
      }

      const firstAvailableTimeSlot = currentDateTimeSlots?.find((slot) => slot.isAvailable)?.slotId || '';
      return {
        ...prev,
        timeSlotId: firstAvailableTimeSlot,
      };
    });
  }, [selectedSlots.dateSlotId, setSelectedSlots, timeslotsData?.dates, timeslotsData?.predefinedSlotObject]);

  useEffect(() => {
    // don't set if we already have selected slots
    if (selectedSlots.dateSlotId && selectedSlots.timeSlotId) return;
    const dateSlotId = selectedDateTimeSlot ? selectedDateTimeSlot.date : timeslotsData?.predefinedDate;

    const currentDate = timeslotsData?.dates?.find((i) => i.date === dateSlotId);

    const timeSlotId = selectedDateTimeSlot
      ? currentDate?.timeslots.find((slot) => slot.slotId === selectedDateTimeSlot.slotId)?.slotId
      : timeslotsData?.predefinedSlotObject?.slotId;

    // only set if both are not set, else don't override the selected value as might only date would be selected.
    if (!selectedSlots.dateSlotId && !selectedSlots.timeSlotId) {
      setSelectedSlots({
        dateSlotId: dateSlotId || '',
        timeSlotId: timeSlotId || '',
      });
    }
  }, [timeslotsData, selectedDateTimeSlot, selectedSlots.dateSlotId, selectedSlots.timeSlotId]);

  useEffect(() => {
    if (show && !isSlotsLoaded) {
      if (dateRefs && dateRefs.current && dateRefs.current[selectedSlots.dateSlotId]) {
        const slotRef = dateRefs.current[selectedSlots.dateSlotId] as HTMLDivElement;
        slotRef.scrollIntoView({ block: 'center' });
      }
      if (slotRefs && slotRefs.current && slotRefs.current[selectedSlots.timeSlotId]) {
        const slotRef = slotRefs.current[selectedSlots.timeSlotId] as HTMLDivElement;
        slotRef.scrollIntoView({ block: 'center' });
        setIsSlotsLoaded(true);
      }
    }
  }, [show, selectedSlots, isSlotsLoaded]);

  const handleDateSelected = (dateSlot: TimeSlots.Date) => {
    if (!dateSlot.isAvailable) return;

    setSelectedSlots((prev) => ({
      ...prev,
      dateSlotId: dateSlot.date,
    }));
  };

  const handleSlotSelected = (slot: TimeSlots.Timeslot) => {
    // check if timeslot
    if (!slot.isAvailable) return;
    setSelectedSlots((prev) => ({
      ...prev,
      timeSlotId: slot.slotId,
    }));
  };

  const DateTimeSlots = timeslotsData?.dates?.find((d) => d.date === selectedSlots.dateSlotId);
  let SelectedSlot = DateTimeSlots?.timeslots.find((slot) => slot.slotId === selectedSlots.timeSlotId);

  // In case timeslots mode changed and slotIds are not matching, then select first available slot
  if (selectedDateTimeSlot && DateTimeSlots && selectedSlots.timeSlotId && !SelectedSlot) {
    SelectedSlot = DateTimeSlots?.timeslots.find((slot) => slot.isAvailable);
  }

  const capitalizeText = (text: string = '') => text.charAt(0).toUpperCase() + text.slice(1);

  return {
    selectedSlots,
    dateRefs,
    slotRefs,
    handleDateSelected,
    handleSlotSelected,
    DateTimeSlots,
    SelectedSlot,
    capitalizeText,
  };
}
